import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCampaigns, sendCampaign, getEmailAccounts, deleteCampaign, updateCampaign } from '../services/api';

function CampaignsList() {
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedAliases, setSelectedAliases] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleting, setDeleting] = useState({});
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, accountsRes] = await Promise.all([
        getCampaigns(),
        getEmailAccounts()
      ]);

      if (campaignsRes.success) {
        setCampaigns(campaignsRes.campaigns || []);
      }

      if (accountsRes.success) {
        setAccounts(accountsRes.accounts || []);
      }
    } catch (err) {
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const openSendModal = (campaign) => {
    setSelectedCampaign(campaign);
    setSelectedAccounts([]);
    setSelectedAliases([]);
    setShowSendModal(true);
    setError('');
    setSuccess('');
  };

  const handleSendCampaign = async () => {
    if (!selectedCampaign) return;

    setSending({ ...sending, [selectedCampaign.id]: true });
    setError('');
    setSuccess('');

    try {
      const accountIds = selectedAccounts.length > 0 ? selectedAccounts : null;
      const aliasIds = selectedAliases.length > 0 ? selectedAliases : null;

      const response = await sendCampaign(selectedCampaign.id, accountIds, aliasIds);
      if (response.success) {
        setSuccess(response.message || 'Campaign sending process started. Check the analysis section for progress.');
        setShowSendModal(false);
        loadData();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.message || 'Failed to send campaign');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send campaign');
    } finally {
      setSending({ ...sending, [selectedCampaign.id]: false });
    }
  };

  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const toggleAlias = (aliasId) => {
    setSelectedAliases(prev =>
      prev.includes(aliasId)
        ? prev.filter(id => id !== aliasId)
        : [...prev, aliasId]
    );
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    let responseConfig = [
      { label: 'Yes', value: 'yes', color: '#10b981' },
      { label: 'No', value: 'no', color: '#ef4444' }
    ];
    if (campaign.response_config) {
      try {
        responseConfig = typeof campaign.response_config === 'string'
          ? JSON.parse(campaign.response_config)
          : campaign.response_config;
      } catch (e) {
        console.error('Error parsing response_config:', e);
      }
    }
    setEditFormData({
      campaignName: campaign.campaign_name || '',
      subjectLine: campaign.subject_line || '',
      htmlContent: campaign.html_content || '',
      responseConfig: responseConfig
    });
    setShowEditModal(true);
    setError('');
    setSuccess('');
  };

  const handleUpdateCampaign = async () => {
    if (!editingCampaign) return;

    if (!editFormData.campaignName || !editFormData.subjectLine || !editFormData.htmlContent) {
      setError('Campaign name, subject line, and HTML content are required');
      return;
    }

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      const response = await updateCampaign(editingCampaign.id, editFormData);
      if (response.success) {
        setSuccess('Campaign updated successfully');
        setShowEditModal(false);
        setEditingCampaign(null);
        setEditFormData({});
        loadData();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.message || 'Failed to update campaign');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update campaign');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Are you sure you want to delete "${campaignName}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting({ ...deleting, [campaignId]: true });
    setError('');
    setSuccess('');

    try {
      const response = await deleteCampaign(campaignId);
      if (response.success) {
        setSuccess('Campaign deleted successfully');
        loadData();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.message || 'Failed to delete campaign');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete campaign');
    } finally {
      setDeleting({ ...deleting, [campaignId]: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-400">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-6 backdrop-blur-sm">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-white mb-2">📧 Email Campaigns</h1>
              <p className="text-sm text-gray-400">Manage your email marketing campaigns</p>
            </div>
            <Link
              to="/campaigns/create"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Campaign
            </Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-blue-400 text-sm">{success}</p>
          </div>
        )}

        {campaigns.length === 0 ? (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-12 text-center backdrop-blur-sm">
            <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-white">No campaigns</h3>
            <p className="mt-2 text-gray-400">Get started by creating your first campaign</p>
            <Link
              to="/campaigns/create"
              className="mt-6 inline-block px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
            >
              Create Campaign
            </Link>
          </div>
        ) : (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg overflow-hidden backdrop-blur-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0a0e27] border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Recipients</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Performance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-[#0a0e27]/50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-white">{campaign.campaign_name}</div>
                          <div className="text-sm text-gray-400">{campaign.subject_line}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-white">
                        {campaign.sent_count || campaign.calculated_sent_count || 0} / {campaign.total_recipients || campaign.calculated_total_recipients || 0}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          campaign.status === 'completed' 
                            ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                            : campaign.status === 'sending'
                            ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                            : 'bg-gray-700/50 text-gray-300 border border-gray-600/30'
                        }`}>
                          {campaign.status || 'draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {campaign.opened_count || 0} opened • {campaign.responded_count || 0} responded
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Link
                            to={`/campaigns/${campaign.id}`}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => handleEditCampaign(campaign)}
                            className="px-3 py-1 text-xs bg-gray-800 text-white rounded hover:bg-gray-700 transition-all"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openSendModal(campaign)}
                            disabled={sending[campaign.id]}
                            className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-all disabled:opacity-50"
                          >
                            {sending[campaign.id] ? 'Sending...' : 'Send'}
                          </button>
                          <button
                            onClick={() => handleDeleteCampaign(campaign.id, campaign.campaign_name)}
                            disabled={deleting[campaign.id]}
                            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-all disabled:opacity-50"
                          >
                            {deleting[campaign.id] ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Send Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-2xl shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4">Send Campaign: {selectedCampaign?.campaign_name}</h2>
              
              {accounts.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Select Email Accounts:</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {accounts.map((account) => (
                      <label key={account.id} className="flex items-center p-3 bg-black/50 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account.id)}
                          onChange={() => toggleAccount(account.id)}
                          className="w-4 h-4 text-blue-600 bg-black border-gray-600 rounded focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm text-white">{account.email_address}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {accounts.length > 0 && accounts.some(a => a.aliases?.length > 0) && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Select Aliases:</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {accounts.map((account) =>
                      account.aliases?.map((alias) => (
                        <label key={alias.id} className="flex items-center p-3 bg-black/50 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedAliases.includes(alias.id)}
                            onChange={() => toggleAlias(alias.id)}
                            className="w-4 h-4 text-blue-600 bg-black border-gray-600 rounded focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-white">{alias.alias_email}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendCampaign}
                  disabled={sending[selectedCampaign?.id]}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50"
                >
                  {sending[selectedCampaign?.id] ? 'Sending...' : 'Send Campaign'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingCampaign && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-[#1a1f3a] border border-blue-500/30 rounded-lg p-6 w-full max-w-3xl shadow-xl my-8">
              <h2 className="text-xl font-semibold text-white mb-6">Edit Campaign</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={editFormData.campaignName}
                    onChange={(e) => setEditFormData({ ...editFormData, campaignName: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subject Line</label>
                  <input
                    type="text"
                    value={editFormData.subjectLine}
                    onChange={(e) => setEditFormData({ ...editFormData, subjectLine: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">HTML Content</label>
                  <textarea
                    value={editFormData.htmlContent}
                    onChange={(e) => setEditFormData({ ...editFormData, htmlContent: e.target.value })}
                    rows={10}
                    className="w-full px-4 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCampaign(null);
                    setEditFormData({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateCampaign}
                  disabled={updating}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all disabled:opacity-50"
                >
                  {updating ? 'Updating...' : 'Update Campaign'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignsList;
